/* eslint-disable no-loop-func */
import osPath from 'path';
import objectPath from 'object-path';
import Alert from 'react-s-alert';
import { observable, action, computed, when } from 'mobx';

import { AppManager, Variables, Input, routerStore } from 'core';
import { analytics } from 'core/analytics';

import { rootStore } from 'apps/store';
import { clientStore } from 'apps/client/app';
import { modalStore } from 'apps/modals/app';
import { ConfirmAction } from 'apps/modals/confirmAction';
import { FormStore } from 'libs/forms/app';
import { FileApp } from 'apps/file/app';
import FileAppManager from 'apps/file/appManager';
import { ReadmeApp } from 'apps/readme/app';
import { RepositoryModel, RepositoryApp } from 'apps/repository/app';
import { ContributionForm } from 'apps/repository/views/contributionForm';
import { ProjectContextModel, ProjectContextApp } from 'apps/context/app';

import {
  RepoQuery,
  PendingOperationsQuery,
  ZipStatusSubscription,
  CommitContributionMutation,
  CancelContributionMutation,
  CreateProjectArchiveMutation,
} from 'apps/repository/gql';

import { conf } from 'apps/repository/config.yaml';
import { SelectTemplateModal } from 'apps/readme/views/selectTemplate';
import { SelectSubtemplateParentModal } from 'apps/readme/views/selectSubtemplateParent';
import templateMap from 'apps/repository/templateHelper';

import { SyncRepoAppManager } from 'apps/repository/syncRepository/appManager';
import { isInDesktopMode } from 'libs/gutClient/electronBridge';

import { OpQueue } from './opQueue';

export class RepoAppManager extends AppManager {
  static registryName = 'RepoAppManager';
  @observable contributorsCount = 0;
  @observable hasError = false;
  @observable folderInputVisible = false;
  @observable newFolderName = '';
  @observable contributionTitle = '';
  @observable contributionDescription = '';
  @observable readme = null;
  @observable cancelling = false;
  @observable committing = false;
  @observable showDownloadAppDialog = false;
  @observable creatingArchive = false;
  opQueue = null;

  @observable syncRepoAppManager = null;

  @observable contributionsConn = null;

  constructor(parent, init = {}, options) {
    super(parent, init, options);
    this.AppRef('repository', RepositoryApp);
    this.AppRef('readme', ReadmeApp);
    this.AppRef('contextApp', ProjectContextApp);
    this.readme = new ReadmeApp(this);

    this.variables = new Variables({
      space: () => this.params.get('profile') || this.params.get('initiative'),
      slug: () => this.params.get('slug'),
      basepath: () => this.params.get('basepath') || '',
      version: () => this.params.get('contributionId') || '',
    });

    this.variables.onChange(['basepath', 'version', 'space', 'slug'], () =>
      this.NewRepoQuery(),
    );

    clientStore.context.set('repoAppManager', this);

    this.opQueue = clientStore.context.get(
      this.variables.space + this.variables.slug,
    );
    if (!this.opQueue) {
      this.opQueue = new OpQueue(this);
      clientStore.context.set(
        this.variables.space + this.variables.slug,
        this.opQueue,
      );
    }

    if (isInDesktopMode()) {
      this.syncRepoAppManager = new SyncRepoAppManager(this);
      this.disposers.push(
        when(
          () => this.parent.project?.id || this.repository?.id,
          () => {
            const repoId = this.parent.project?.id || this.repository?.id;
            this.syncRepoAppManager.init(repoId);
          },
        ),
      );
    }

    // this.disposers.push(this.opQueue.stop);
  }

  @computed get loading() {
    const query = this.queries.get('repo') || this.queries.get('version');
    return query?.loading;
  }

  @computed get atHead() {
    return !this.variables.version;
  }

  @computed get contributionReady() {
    return this.opQueue.finished && !this.cancelling;
  }

  @action NewOperationsQuery = () => {
    if (this.operationsQuery) {
      this.operationsQuery.disposer();
    }
    this.operationsQuery = new PendingOperationsQuery(this, {
      queryKey: 'operations',
      onSysErr: this.onError,
      variables: this.variables,
    });
    this.operationsQuery.process(
      result => RepositoryModel.Load(result),
      this.RepoLoad,
    );
    this.operationsQuery.query();
  };

  @action NewRepoQuery = afterLoadCallback => {
    if (this.repoQuery) {
      this.repoQuery.disposer();
    }
    this.repoQuery = new RepoQuery(this, {
      queryKey: 'repo',
      onSysErr: this.onError,
      variables: this.variables,
    });
    this.repoQuery.process(
      result => {
        return RepositoryModel.Load(result);
      },
      repo => {
        this.RepoLoad(repo);
        // - previously the conflicts manager redirect back to the .files (thats us)
        // page, but because this manager can potentially redirect back to the
        // .conflicts (thats them) page, we could end up with a redirect loop
        // - so I now make it so conflicts manager passed afterLoadCallback to this
        // function to explicitly a.) update the repo state and b.) redirect back to
        // .files once we're done resolving conflicts
        if (repo && afterLoadCallback) {
          afterLoadCallback(repo);
        }
      },
    );
    this.repoQuery.query();

    this.repoQuery.process(
      context => (context ? ProjectContextModel.Load(context) : null),
      this.UpdateContext,
      { subPath: 'context' },
    );
  };

  createProjectArchiveError = () => {
    Alert.error('Failed to download.');
  };

  createProjectArchiveSuccess = () => {
    this.SubscribeZipStatus();
  };

  @action getCurrentApp = () => {
    const { apps } = this?.contributionsConn?.arrayApp ?? {};

    const currentApp = apps?.find(
      app =>
        app.version.substring(0, 7) === routerStore.route.params.contributionId,
    );

    return currentApp;
  };

  @action createProjectArchive = () => {
    const currentApp = this.getCurrentApp();
    let version =
      currentApp?.version || this.repository.contributionUpstream.version;

    version = version.substring(0, 7);
    const input = new Input({
      input: {
        projectId: this.repository.id,
        version,
      },
    });

    const options = {
      onSysErr: this.createProjectArchiveError,
      onUserErr: this.createProjectArchiveError,
      onSuccess: this.createProjectArchiveSuccess,
    };

    const mutation = new CreateProjectArchiveMutation(
      input,
      null,
      options,
      true,
    );

    mutation.mutate();

    this.creatingArchive = true;
  };

  @action SubscribeZipStatus = () => {
    if (this.zipStatusSubscription) {
      this.zipStatusSubscription.CompleteSubscription();
      this.zipStatusSubscription = null;
    }

    if (!this.contextApp || !this.repository?.contributionUpstream?.id) {
      return;
    }

    this.zipStatusSubscription = new ZipStatusSubscription(this, {
      variables: {
        input: {
          contextId: this.contextApp?.id,
          contributionId: this.repository.contributionUpstream?.id,
        },
      },
    });

    this.zipStatusSubscription.StartSubscription();

    this.zipStatusSubscription.process(zipArchiveUrl => {
      const downloadPath = new URL(zipArchiveUrl).pathname;
      this.ZipUpdate(downloadPath);

      // auto download when subscribe zip status back
      const a = document.createElement('a');
      // simulate download button behaviour
      a.href = downloadPath;
      a.download = '';
      a.click();
    });
  };

  @action ZipUpdate = zipArchiveUrl => {
    if (
      objectPath.get(this.repository, 'contributionUpstream') &&
      zipArchiveUrl
    ) {
      this.repository.contributionUpstream.upstream.zipArchiveUrl =
        zipArchiveUrl;
      this.zipStatusSubscription.disposer();
      this.zipStatusSubscription = null;
      this.creatingArchive = false;
    }
  };

  @action CatchError = error => {
    this.hasError = true;
    analytics.logError(error);
  };

  @action UpdateContext = contextModel => {
    if (!contextModel) return;
    this.contextApp = contextModel;
  };

  @action RepoLoad = repository => {
    if (!repository) return;
    this.repository = repository;

    // Set-up readme based on data from upstream
    const filename = 'README.md';
    const readmeRepoItem = this.repository.FindFileCWD(filename);
    const reference = readmeRepoItem?.reference || {
      origin: null,
      file: null,
    };
    // TODO: I don't know why reference file can be null, this is a workaround that issue.
    if (!reference.file && readmeRepoItem?.contributionFile?.file) {
      reference.file = readmeRepoItem?.contributionFile?.file;
      reference.origin = readmeRepoItem?.contributionFile?.origin;
    }

    if (!readmeRepoItem) {
      this.readme.setInitialized(false);
    }
    const projectId = this.repository.id;
    const dirname = this.variables.basepath;
    this.readme.dirname = dirname;
    this.readme.filename = filename;
    this.readme.projectId = projectId;
    this.readme.origin = reference.origin;
    this.readme.file = reference.file;
    this.readme.initialize();

    const { hasOpenContribution, contributionUpstream } = this.repository;
  };

  @computed get contributionUpstreamFilesByFilename() {
    const files = this.repository?.contributionUpstream?.files;
    const map = {};
    files?.forEach(f => {
      map[f.filename] = f;
    });
    return map;
  }

  @computed get repoCwdFilesByFilename() {
    const cwdFiles = this.repository?.cwdFiles;
    const map = {};
    cwdFiles?.forEach(f => {
      map[f.contributionFile.filename] = f;
    });
    return map;
  }

  setDownloadDialogVisible = visible => {
    this.showDownloadAppDialog = visible;
  };

  CancelDrafts() {
    if (!(clientStore.signedInProfile && this.repository)) return;
    const repoDraftKeys = Object.keys(localStorage).filter(key =>
      key.startsWith(
        [clientStore.signedInProfile.username, this.repository.id].join('-'),
      ),
    );
    repoDraftKeys.forEach(draftKey => {
      localStorage.removeItem(draftKey);
    });
  }

  @action CreateReadme = () => {
    analytics.sendEvent('created-readme');
    const dirname = this.variables.basepath;
    const title = dirname ? dirname.split('/').pop() : this.parent.project.name;
    this.readme.initialize({ content: `# ${title}`, sync: true });
    this.readme.Edit();
  };

  @computed get contributionCount() {
    return this.repository ? this.repository.contributionCount : 0;
  }

  @computed get contributorCount() {
    return this.repository ? this.repository.contributorCount : 0;
  }

  @action SetActiveTab = tabName => {
    this.activeTab = tabName;
  };

  @action SetFolderInputVisibility = visible => {
    if (visible !== this.folderInputVisible) {
      this.newFolderName = '';
      this.folderInputVisible = visible;
      this.folderForm = visible
        ? new FormStore(this, conf, 'newFolderForm')
        : null;
    }
  };

  @action CreateFolder = () => {
    if (this.folderForm.isValid) {
      const folderPath = osPath.join(
        this.variables.basepath,
        // - windows does not allow leading/trailing whitespace (but only 0x20),
        // and also no trailing period (0x2E)
        // - https://learn.microsoft.com/en-us/troubleshoot/windows-client/shell-experience/file-folder-name-whitespace-characters
        this.newFolderName.replace(/^ +|[ .]+$/g, ''),
      );
      this.opQueue.QueueOperation(this.repository, 'ADD', { path: folderPath });
    }
    this.SetFolderInputVisibility(false);
  };

  @action ContributionForm = () => {
    this.form = new FormStore(this, conf, 'contributionForm');
    modalStore.SetModalComponent(ContributionForm, { manager: this });
  };

  @action OnContributionError = error => {
    if (error.extensions?.code === 'UPLOAD_INCOMPLETE') {
      Alert.error(
        'Some of your uploads did not complete. You can fix that by uploading the same files again. Any file that was already uploaded is automatically skipped when uploaded again.',
        {
          timeout: 20000,
        },
      );
    } else if (error.extensions?.code === 'LOCKED_FILES') {
      const gqlError = error;
      let filesText = '';
      const filesData = gqlError.extensions.locked_paths;
      if (filesData) filesText = Object.keys(filesData).join(', ');
      const msgLength = 40;
      if (filesText.length > msgLength)
        filesText = `${filesText.substring(0, msgLength - 3)}...`;

      Alert.error(
        `Can't contribute because some files are locked: ${filesText}`,
        {
          timeout: 20000,
        },
      );
    } else {
      Alert.error('Whoops! There was an error contributing. Please try again.');
    }
    analytics.logError(error);

    this.NewRepoQuery();

    this.committing = false;
  };

  @action OnCommitSuccess = result => {
    this.form?.clearDraft();
    this.contributionTitle = '';
    this.contributionDescription = '';

    // send to HubSpot?
    analytics.sendEvent('contributed', {}, { ga: true });
    analytics.sendEvent(
      'Donate',
      { currency: 'EUR', value: 12 },
      { facebook: true },
    );
    if (this.opQueue.fileManagers.length > 0) {
      analytics.sendEvent(
        'uploaded-file',
        {
          count: this.opQueue.fileManagers.length,
        },
        {
          ga: true,
        },
      );
    }

    this.CancelDrafts();
    if (result) {
      this.RepoLoad(RepositoryModel.Load(result));
      rootStore.stores.processMonitor.NewProcessMonitorQuery();
    }

    this.opQueue.localOperations.clear();
    this.opQueue.clear();

    this.committing = false;
    this.cancelling = false;
  };

  @action CommitContribution = () => {
    const { form } = this;
    if (form.isValid) {
      const input = new Input({
        commitData: {
          projectId: this.repository.id,
          title: this.contributionTitle,
          description: this.contributionDescription,
        },
        basepath: this.variables.basepath,
        version: this.variables.version,
      });
      const options = {
        onSuccess: result => {
          if (result.conflicts.length > 0) {
            // - when the commit comes back with conflicts, then we redirect to the
            // .conflicts page, so set committing false and then let the conflicts
            // manager handle the conflict resolution commit
            this.committing = false;
            this.cancelling = false;

            Alert.error(
              'There are conflicts that need to be resolved before you can contribute.',
            );

            const spaceType = this.params.get('profile')
              ? 'profile'
              : 'initiative';
            routerStore.navigate(`${spaceType}.project.conflicts`, {
              [`${spaceType}`]:
                this.params.get('profile') || this.params.get('initiative'),
              slug: this.params.get('slug'),
            });
          } else {
            // - no conflicts, so we can finalize the commit
            this.OnCommitSuccess(result);
          }
        },
        onSysErr: this.OnContributionError,
        onUserErr: this.OnContributionError,
      };
      const mutation = new CommitContributionMutation(
        input,
        null,
        options,
        true,
      );
      this.committing = true;
      mutation.mutate();
      modalStore.Close();
      this.mutations.push(mutation);
      return mutation;
    }
    form.alert();
    return false;
  };

  @action onCancelError = error => {
    Alert.error(
      'Whoops! There was an error cancelling your contribution. Please try again.',
    );
    analytics.logError(error);

    this.NewRepoQuery();

    this.opQueue.start();
    this.cancelling = false;
  };

  @action CancelContribution = () => {
    this.opQueue.stop();

    const options = {
      onSuccess: result => {
        this.CancelDrafts();

        rootStore.stores.processMonitor.NewProcessMonitorQuery();
        RepositoryModel.Load(result);

        this.opQueue.localOperations.clear();
        this.opQueue.start();

        this.cancelling = false;
        this.committing = false;
      },
      onSysErr: this.onCancelError,
      onUserErr: this.onCancelError,
    };
    const input = new Input({
      resetData: {
        projectId: this.repository.id,
      },
      basepath: this.variables.basepath,
      version: this.variables.version,
    });
    const mutation = new CancelContributionMutation(input, null, options, true);
    this.cancelling = true;
    mutation.mutate();
    this.mutations.push(mutation);
    return mutation;
  };

  @action ConfirmCancelContribution = () => {
    modalStore.SetModalComponent(ConfirmAction, {
      callBack: () => this.CancelContribution(),
      title: 'Reset your Contribution?',
      undo: true,
      text: 'This will discard all your local changes.',
      actionName: 'Accept',
    });
  };

  @action AddFiles = (
    localFiles,
    options = { immediate: false, uploadCallback: () => {} },
    replaceFilenames,
  ) => {
    // - when we AddFiles and already have at least one operation pending, then we
    // schedule this when to check if after the add we end up without an open
    // contribution and nothing pending, if we do, then we need to cancel the
    // contribution
    // - we do this because a rm and an add for the same path cancel each other out
    // and we want the server to delete the staging area
    // - it can't figure it out on its own currently, this is a temporary hack for more
    // performance when uploading a lot of files
    if (this.repository.pendingOperations.length > 0 && localFiles.length > 0) {
      this.disposers.push(
        when(
          () =>
            !this.hasOpenContribution &&
            this.contributionReady &&
            this.repository.pendingOperations.length === 0,
          () => {
            this.CancelContribution();
          },
        ),
      );
    }

    const sortedFiles = Array.from(localFiles);
    sortedFiles.sort((a, b) => {
      const aPath = a.webkitRelativePath || a.fullPath;
      const bPath = b.webkitRelativePath || b.fullPath;
      return bPath.localeCompare(aPath);
    });
    for (let i = 0; i < sortedFiles.length; i++) {
      const localFile = sortedFiles[i];
      let replaceFilename = null;
      if (replaceFilenames && replaceFilenames.length > i) {
        replaceFilename = replaceFilenames[i];
      }
      this.addFile(localFile, options, replaceFilename);
    }
  };

  // - you are not supposed to call this, you need to call AddFiles with list
  // arguments so that the when for cleaning up the contribution in case the
  // ops cancel each other out is created
  @action addFile = (
    localFile,
    options = {
      immediate: false,
      uploadCallback: () => {},
    },
    replaceFilename,
  ) => {
    const { immediate, uploadCallback } = options;
    const { basepath } = this.variables;

    // - we want to make changes to localFile, which is from an input and is
    // readonly, so we just copy it
    const localFileCopy = { ...localFile };

    const { webkitRelativePath } = localFileCopy;

    // - replace the filename so we can take an existing contributionFile and
    // replace it with something else, only the filename has to change then,
    // all other stuff like size, type, lastModified should be handled as if
    // we're uploading a new file

    if (replaceFilename) {
      // - we need to replace the localFile.name, the localFile.fileObject.name
      // (which is what is used to the fileAppManager.filename in SetLocalFile)
      // so that the uploaded file gets the same filename as replaceFilename,
      // meaning it will be downloaded/converted as replaceFilename
      localFileCopy.name = replaceFilename;
      // - localFileCopy.fileObject is readonly again, change the prop to writeable
      // and then change it
      Object.defineProperty(localFileCopy.fileObject, 'name', {
        writable: true,
        value: replaceFilename,
      });

      // - we build the projectPath and the path in the contribution from
      // webkitRelativePath || localFileCopy.fullPath, so in order to get
      // the renameFilename in the contribution we need to change those
      // depending on which the later code will pick as projectPath/filePath
      if (webkitRelativePath) {
        const replaceBasePath = webkitRelativePath.substring(
          0,
          webkitRelativePath.lastIndexOf('/'),
        );
        localFileCopy.webkitRelativePath = osPath.join(
          replaceBasePath,
          replaceFilename,
        );
      } else {
        const replaceBasePath = localFileCopy.fullPath.substring(
          0,
          localFileCopy.fullPath.lastIndexOf('/'),
        );
        localFileCopy.fullPath = osPath.join(replaceBasePath, replaceFilename);
      }
    }

    // If webkitRelativePath is present, it comes from a folder upload process
    // Then, we need to make sure that the internal folder structure exists
    if (webkitRelativePath) {
      // Update the internal file fullpath from the current folder
      localFileCopy.fullPath = webkitRelativePath;
    }

    // - basepath is the path we're looking at in the repo, so to get the full
    // path inside the repo we need to append the fullPath to it
    // - we're in Folder/ the user uploads Design/File.stl, then we need to create
    // Folder/Design/File.stl
    const filePath = osPath.join(basepath, localFileCopy.fullPath);
    // - I don't know what this is for and why we use webkitRelativePath here
    const projectPath = webkitRelativePath || filePath;

    const fileManager = new FileAppManager(null, {
      formTarget: this.parent.project,
      projectPath,
    });

    if (immediate) {
      clientStore.uploader.FastTrackFile(
        fileManager,
        localFileCopy,
        uploadCallback,
      );
    } else {
      clientStore.uploader.QueueFile(
        fileManager,
        localFileCopy,
        uploadCallback,
      );
    }

    this.queueAddOperation(filePath, fileManager);
  };

  @action queueAddOperation = (filePath, fileManager) => {
    const { file } = fileManager;
    this.opQueue.QueueOperation(
      this.repository,
      'ADD',
      {
        path: filePath,
        file,
      },
      fileManager,
    );
  };

  @action DeleteFile = fileInfo => {
    if (this.repository.pendingOperations.length > 0) {
      // - same as above in AddFiles, same reason, an add and a rm for the
      // same path cancel each other out, and then we need to tell the server
      // to clear the staging area
      this.disposers.push(
        when(
          () =>
            !this.hasOpenContribution &&
            this.contributionReady &&
            this.repository.pendingOperations.length === 0,
          () => {
            this.CancelContribution();
          },
        ),
      );
    }

    const { reference } = fileInfo;
    // eslint-disable-next-line
    const { path } = reference;
    this.opQueue.QueueOperation(this.repository, 'REMOVE', {
      path,
      file: reference,
    });
  };

  @action MoveFile = (fileInfo, destinationInfo) => {
    const destination = destinationInfo.reference;
    if (!destination.isFolder) return;
    const file = fileInfo.reference;
    const oldPath = file.path;
    const newPath = osPath.join(destination.path, file.filename);
    this.opQueue.QueueOperation(this.repository, 'RENAME', {
      oldPath,
      path: newPath,
      file: file.file,
    });
  };

  @action RenameFile = (fileInfo, newName) => {
    if (this.repository.pendingOperations.length > 0) {
      this.disposers.push(
        when(
          () =>
            !this.hasOpenContribution &&
            this.contributionReady &&
            this.repository.pendingOperations.length === 0,
          () => {
            this.CancelContribution();
          },
        ),
      );
    }

    const { reference } = fileInfo;
    const oldPath = reference.path;
    const newPath = osPath.join(reference.dirname, newName);
    if (oldPath !== newPath) {
      this.opQueue.QueueOperation(this.repository, 'RENAME', {
        oldPath,
        path: newPath,
        file: reference.file,
      });
    }
    reference.SetRenaming(false);
  };

  @action onResetFileError = error => {
    Alert.error(
      'Whoops! There was an error resetting the file. Please try again.',
    );
    analytics.logError(error);

    this.NewRepoQuery();
    this.cancelling = false;
  };

  @action ResetFile = fileInfo => {
    const { reference } = fileInfo;
    // eslint-disable-next-line no-shadow
    const { path } = reference;
    const input = new Input({
      resetData: {
        projectId: this.repository.id,
        path,
      },
      basepath: this.variables.basepath,
      version: this.variables.version,
    });
    const options = {
      onSuccess: result => {
        this.opQueue.RemoveOperations(this.repository, {
          path,
          file: reference,
        });
        rootStore.stores.processMonitor.NewProcessMonitorQuery();
        RepositoryModel.Load(result);
        this.cancelling = false;
      },
      onSysErr: this.onResetFileError,
      onUserErr: this.onResetFileError,
    };
    const mutation = new CancelContributionMutation(input, null, options, true);
    this.cancelling = true;
    mutation.mutate();
    this.mutations.push(mutation);
    return mutation;
  };

  @action OpenReadmeTemplates = () => {
    this.readme.init({ content: '', sync: true });
    modalStore.SetModalComponent(SelectTemplateModal, { readmeApp: this });
  };

  @action OpenSubtemplates = (parentTemplate, editing = false) => {
    let templateToUse = parentTemplate;

    /* Since we are not storing the original template, for the moment we assume
    it will be the osh one.
    In order to find if the subsections are present, we check if there is
    any section in the readme, whose title is the same as the one defined
    in the config.yaml file */

    if (!parentTemplate) {
      templateToUse = conf.templates.oshDocumentationGuidelines;
    }
    const filledSections = []; // Contains the ids of the already filled sections
    if (editing) {
      // Find the already filled sections

      const { sections } = conf.templates.oshDocumentationGuidelines;

      Object.keys(conf.templates.oshDocumentationGuidelines.sections).forEach(
        templateId => {
          const readmeHasTitle = this.readme.hasSectionWithTitle(
            sections[templateId].title,
          );
          if (readmeHasTitle) {
            filledSections.push(templateId);
          }
        },
      );
    }

    modalStore.SetModalComponent(SelectSubtemplateParentModal, {
      readmeApp: this,
      parentTemplate,
      subsections: templateToUse.sections,
      clickConfirm: this.OnSubtemplateConfirm,
      subtemplateSelected: this.OnSubtemplateSelected,
      userJourneysData: templateToUse.userJourneys,
      filledSections,
    });
  };

  @action getDefaultTemplate = () => {
    return null;
  };

  getContentOfFile = async (templateid, filename) => {
    const templateFile = new FileApp(null, {
      id: `nr-readme-template-${filename}`,
      permalink: templateMap[filename],
    });
    await templateFile.readAsText();

    return { templateid, content: templateFile.fileAsText };
  };

  getFullContent = async templatesInfo => {
    const filesArray = [];

    const templateKeys = Object.keys(templatesInfo);

    for (let i = 0; i < templateKeys.length; i += 1) {
      const templateId = templateKeys[i];

      if (templatesInfo[templateId].isSelected === true) {
        const templateInfo = templatesInfo[templateId];
        const templateFile = new FileApp(null, {
          id: `readme-template-${templateId}`,
          permalink: templateMap[templateInfo.file],
        });
        filesArray.push(templateFile);
      }
    }

    // Iterate through the files and generate the content
    let content = '';

    for (let i = 0; i < filesArray.length; i += 1) {
      const file = filesArray[i];

      // eslint-disable-next-line no-await-in-loop
      await file.readAsText();
      // eslint-disable-next-line no-loop-func
      content += file.fileAsText;
      content += '\n';
    }

    return content;
  };

  processAlreadyExistingSubtemplates = (
    templatesInfo,
    previouslyFilledSections,
  ) => {
    /* We need to extract the individual sectionApps for each subsection
         and in order to do so, we need to know the extension in the array
         of each of the templates.
         */
    const sectionIndexArray = [];
    this.readme.sectionApps.forEach((sectionapp, index) => {
      if (previouslyFilledSections.includes(sectionapp.title.toLowerCase())) {
        sectionIndexArray.push({
          section: sectionapp.title.toLowerCase(),
          index,
        });
      }
    });

    /* Once we have the information (e.g. The "USE" section goes from
             index 12 to 23), we extract the sectionApps from the readmes */
    const sectionAppsForEachSection = {};
    sectionIndexArray.forEach((sectionindex, i) => {
      const sectionId = sectionindex.section;
      const indexForSection = sectionindex.index;

      // Depending on the position in the array, we need to go...

      let apps;
      // from 0th element to the start of the second section
      if (indexForSection === 0) {
        apps = this.readme.sectionApps.slice(0, sectionIndexArray[i + 1].index);
        // from the start of the last section to the end of the array
      } else if (i === sectionIndexArray.length - 1) {
        apps = this.readme.sectionApps.slice(
          indexForSection,
          this.readme.sectionApps.length,
        );
      } else {
        // and from the index of the section to the beginning of the next one
        apps = this.readme.sectionApps.slice(
          indexForSection,
          sectionIndexArray[i + 1].index,
        );
      }
      sectionAppsForEachSection[sectionId] = apps;
    });

    const newSectionAppsMap = {};
    const promises = [];
    // Now, we iterate trough the templates information.
    Object.keys(templatesInfo).forEach(key => {
      const sectionInfo = templatesInfo[key];

      // If the section appears as "selected" and we already have the sections...
      if (sectionInfo.isSelected && previouslyFilledSections.includes(key)) {
        newSectionAppsMap[key] = sectionAppsForEachSection[key];
      } else if (
        sectionInfo.isSelected &&
        !previouslyFilledSections.includes(key)
      ) {
        // Otherwise, we need to actually create those sections
        promises.push(this.getContentOfFile(key, sectionInfo.file));
      }
    });

    Promise.all(promises).then(contentArray => {
      contentArray.forEach(info => {
        // Once we have finished reading the file for each template,
        // create a new temporal readme and "steal" its sections

        const newreadme = new ReadmeApp({});

        newreadme.initialize({ content: info.content });
        newSectionAppsMap[info.templateid] = [...newreadme.sectionApps]; // To de-proxify
      });

      // Finally, we do a last iteration through the templates information
      // (we have now all the information about the sections) and override the
      // sectionApps of the readme. By doing this, we ensure to keep the same
      // order as defined in the yaml file
      const finalOrderedSectionApps = [];
      Object.keys(templatesInfo).forEach(key => {
        const sectionInfo = templatesInfo[key];
        if (sectionInfo.isSelected) {
          const apps = newSectionAppsMap[key];
          finalOrderedSectionApps.push(...apps);
        }
      });
      // console.log(finalOrderedSectionApps);
      // Override the apps
      this.readme.setSections(finalOrderedSectionApps);
      this.readme.Edit();
      modalStore.ClearComponent();
    });
  };

  @action OnSubtemplateConfirm = (templatesInfo, previouslyFilledSections) => {
    /* TemplatesInfo is the original JSON Object, which is constant
       PreviouslyFilledSections contains a list of those sections that are already
       in the readme */

    if (!previouslyFilledSections || previouslyFilledSections.length === 0) {
      // If there are no old templates, we can do the old way
      // Just generate the whole readme file with the selection
      this.getFullContent(templatesInfo).then(content => {
        this.readme.initialize({ content, sync: true });
        this.readme.Edit();

        modalStore.ClearComponent();
      });
    } else {
      this.processAlreadyExistingSubtemplates(
        templatesInfo,
        previouslyFilledSections,
      );
    }
  };

  @action ChooseTemplate = selectedTemplate => {
    const templateFile = new FileApp(null, {
      id: `readme-template-${selectedTemplate.title}`,
      permalink: templateMap[selectedTemplate.file],
    });
    this.readme.file = templateFile;
    this.readme.initialize();

    this.readme.Edit();
  };
}
